import Reflux from "reflux";
import UIActions from "../actions/UIActions";
import SiteActions from "../actions/SiteActions";
import DocumentStore from "../document/DocumentStore";
import { COMMANDS } from "../document/commands";
import ModelFactory from "../models/ModelFactory";
import AppConstants from "../constants/AppConstants";
import UserActions from "../actions/UserActions";
import DesignChooserActions from "../actions/DesignChooserActions";
import { getBlogType } from "../hooks/query/useAccount";
import { isIntercomEnabled } from "../adaptors/AnalyticsAdaptors";
import {
  STAGE_OVERLAY_CENTER,
  STAGE_OVERLAY_COLUMN,
  STAGE_OVERLAY_GUIDES,
  STAGE_OVERLAY_MOBILE,
  STAGE_OVERLAY_SNAP,
  ZOOM_INCREMENT,
  UI_SCALE_LARGE,
  UI_SCALE_REGULAR,
  UI_SCALE_SMALL,
} from "../constants/UIConstants";
import ActiveBlockContainerStore from "./ActiveBlockContainerStore.mjs";
import SiteSettingsActions from "../actions/SiteSettingsActions";
import SiteDesignStore from "./SiteDesignStore";
import DesignViewStore from "./DesignViewStore";
import { FORM_FIELD_MENU_ITEMS, FORM_SUBMIT_MENU_ITEM } from "../constants/FormFieldConstants.mjs";
import { addFormFieldElement, addSubmitButton } from "../utils/elementCreationUtils";
import BlockUtils from "../utils/BlockUtils";

const {
  BLOG_TYPE_NONE,
  DESIGN_TYPE_PAGE,
  DESIGN_TYPE_WP_PAGE,
  ELEMENT_TYPE_SOCIAL_GRID,
  ELEMENT_TYPE_SIMPLE,
  ELEMENT_TYPE_GRAPHIC,
  ELEMENT_TYPE_LINE,
  ELEMENT_TYPE_ICON,
  ELEMENT_TYPE_IFRAME,
  ELEMENT_TYPE_VIDEO,
  ELEMENT_TYPE_LOTTIE,
  ELEMENT_TYPE_GALLERY,
  MAIN_VIEW_USER_SITE_CHOOSER,
  MAIN_VIEW_LIBRARY_SITE_CHOOSER,
  MODAL_USER_EXPORTS,
  MODAL_DESIGN_IMPORT,
  PANEL_SECTION_ELEMENT_SOCIAL_GRID,
  STAGE_VIEW_SPLIT,
  STAGE_VIEW_MOBILE,
  STAGE_VIEW_DESKTOP,
  TEXT_STYLE_TITLE,
  TEXT_STYLE_HEADING,
  TEXT_STYLE_SUBHEADING,
  TEXT_STYLE_PARAGRAPH,
  THEME_TAB_FONTS,
  THEME_TAB_STYLE,
  USER_SITES_VIEW_MODE_MANAGE,
} = AppConstants;

const BASE_ELEMENT_CREATION_TAGS = ["element", "add", "create"];

const addTextElement = (style) => {
  const element = ModelFactory.getTextElement({ style });
  DocumentStore.dispatch({ type: COMMANDS.ADD_ELEMENT, payload: { element, isNew: true } });
};

const addElement = (elementType) => {
  const element = ModelFactory.getElement(elementType);

  UserActions.trackPostHogEvent("add_element", {
    clickLocation: "command_palette",
    elementType,
  });

  DocumentStore.dispatch({ type: COMMANDS.ADD_ELEMENT, payload: { element, isNew: true } });
  if (elementType === ELEMENT_TYPE_SOCIAL_GRID) {
    UIActions.toggleElementPropertiesSection({
      name: PANEL_SECTION_ELEMENT_SOCIAL_GRID,
    });
  }
};

const addButton = () => {
  const element = ModelFactory.getButton();
  DocumentStore.dispatch({ type: COMMANDS.ADD_ELEMENT, payload: { element, isNew: true } });
};

const showAssetPanel = () => {
  const mediaLibraryConfig = {
    title: "Add Elements",
    btnLabel: "Add to Page",
  };
  const blockContainer = ActiveBlockContainerStore.getContainer();
  if (blockContainer.get("isBlock")) {
    mediaLibraryConfig.btnLabel = "Add To Site Canvas";
  }
  UIActions.toggleAssetChooser(mediaLibraryConfig);
};

const elementCommands = [
  // Text Elements
  {
    name: "Add title element",
    action: () => addTextElement(TEXT_STYLE_TITLE),
    tags: [...BASE_ELEMENT_CREATION_TAGS, "text", "title", "h1"],
    icon: "textElement",
  },
  {
    name: "Add heading element",
    action: () => addTextElement(TEXT_STYLE_HEADING),
    tags: [...BASE_ELEMENT_CREATION_TAGS, "text", "heading", "h2"],
    icon: "textElement",
  },
  {
    name: "Add subheading element",
    action: () => addTextElement(TEXT_STYLE_SUBHEADING),
    tags: [...BASE_ELEMENT_CREATION_TAGS, "text", "subheading", "subtitle", "h3"],
    icon: "textElement",
  },
  {
    name: "Add paragraph element",
    action: () => addTextElement(TEXT_STYLE_PARAGRAPH),
    tags: [...BASE_ELEMENT_CREATION_TAGS, "text", "paragraph", "p", "content"],
    icon: "textElement",
  },
  // Other Elements
  {
    name: "Add rectangle element",
    action: () => addElement(ELEMENT_TYPE_SIMPLE),
    tags: [...BASE_ELEMENT_CREATION_TAGS, "shape", "rectangle", "circle", "simple"],
    icon: "rectangleElement",
  },
  {
    name: "Add button element",
    action: addButton,
    tags: [...BASE_ELEMENT_CREATION_TAGS, "button", "link", "call-to-action", "cta"],
    icon: "buttonElement",
  },
  {
    name: "Add line element",
    action: () => addElement(ELEMENT_TYPE_LINE),
    tags: [...BASE_ELEMENT_CREATION_TAGS, "rule", "divider", "separator", "hr", "dash"],
    icon: "lineElement",
  },
  {
    name: "Add image",
    action: () => addElement(ELEMENT_TYPE_GRAPHIC),
    tags: [...BASE_ELEMENT_CREATION_TAGS, "image", "picture", "photo", "graphic", "jpeg", "png"],
    icon: "mediaLibrary",
  },
  {
    name: "Add icon element",
    action: () => addElement(ELEMENT_TYPE_ICON),
    tags: [...BASE_ELEMENT_CREATION_TAGS, "icon", "symbol", "logo", "emoji", "svg"],
    icon: "symbolElement",
  },
  {
    name: "Add embed element",
    action: () => addElement(ELEMENT_TYPE_IFRAME),
    tags: [...BASE_ELEMENT_CREATION_TAGS, "embed", "iframe", "code", "html", "script"],
    icon: "codeElement",
  },
  {
    name: "Add video element",
    action: () => addElement(ELEMENT_TYPE_VIDEO),
    tags: [...BASE_ELEMENT_CREATION_TAGS, "video", "youtube", "vimeo", "mp4", "media"],
    icon: "videoElement",
  },
  {
    name: "Add lottie element",
    action: () => addElement(ELEMENT_TYPE_LOTTIE),
    tags: [...BASE_ELEMENT_CREATION_TAGS, "lottie", "animation", "motion", "animated"],
    icon: "mediaLibrary",
  },
  {
    name: "Add media from library",
    action: () => showAssetPanel(),
    tags: [
      ...BASE_ELEMENT_CREATION_TAGS,
      "image",
      "picture",
      "photo",
      "asset",
      "media",
      "graphic",
      "jpeg",
      "png",
    ],
    icon: "mediaLibrary",
  },
  {
    name: "Add gallery",
    action: () => addElement(ELEMENT_TYPE_GALLERY),
    tags: [
      ...BASE_ELEMENT_CREATION_TAGS,
      "gallery",
      "images",
      "photos",
      "pictures",
      "sliding",
      "tiled",
    ],
    icon: "mediaLibrary",
  },
  {
    name: "Add social grid",
    action: () => addElement(ELEMENT_TYPE_SOCIAL_GRID),
    tags: [...BASE_ELEMENT_CREATION_TAGS, "instagram", "social", "media"],
    icon: "socialElement",
  },
];

const FORM_FIELD_CREATION_TAGS = [...BASE_ELEMENT_CREATION_TAGS, "form", "field"];

/**
 * Whether form fields can go on the block an add would target: the selected block, else the
 * page's first. Shared by the palette and the footer; the stage and layers menus hide form fields
 * on site canvas instances the same way.
 * @returns {boolean}
 */
export const canAddFormFields = () => {
  const blockContainer = SiteDesignStore.getBlockContainer();
  const block = blockContainer?.getBlock(SiteDesignStore.getAddElementBlockId());
  return !block || !BlockUtils.hasSiteBlock(block);
};

/** Kept apart from `staticCommands`: `CommandPalette` adds them only while the forms flag is on. */
export const formFieldCommands = [
  ...FORM_FIELD_MENU_ITEMS.map(({ id, icon, label, field }) => ({
    name: `Add ${label.toLowerCase()} form field`,
    action: () => addFormFieldElement(field, {}, "command_palette"),
    tags: [...FORM_FIELD_CREATION_TAGS, "input", ...id.split("-")],
    icon,
  })),
  {
    name: "Add form submit button",
    action: () => addSubmitButton({}, "command_palette"),
    tags: [...FORM_FIELD_CREATION_TAGS, "submit", "button", "send"],
    icon: FORM_SUBMIT_MENU_ITEM.icon,
  },
];

const addPage = () => {
  DocumentStore.dispatch({ type: COMMANDS.ADD_PAGE });
  UserActions.trackEvent({ event: "page-add" });
};

const addFolder = () => {
  SiteActions.addPageFolder();
};

const choosePage = () => {
  DesignChooserActions.openDialog({
    title: "Choose A Page",
    designType: DESIGN_TYPE_PAGE,
  });
};

const hasBlog = getBlogType() !== BLOG_TYPE_NONE;

const PageScopeCommands = [
  {
    name: "Add blank page",
    action: addPage,
    tags: ["add", "create", "new", "page", "blank"],
    icon: "add",
  },
  {
    name: "Add page folder",
    action: addFolder,
    tags: ["add", "create", "new", "page", "folder"],
    icon: "add",
  },
  {
    name: "Add page from library",
    action: choosePage,
    tags: ["add", "create", "choose", "new", "page", "template"],
    icon: "add",
  },
];

const addTemplate = () => {
  DocumentStore.dispatch({ type: COMMANDS.ADD_PAGE, payload: { toWP: true } });
  UserActions.trackEvent({ event: "page-add-wp" });
};

const addTemplateFolder = () => {
  DocumentStore.dispatch({ type: COMMANDS.ADD_PAGE, payload: { isWp: true } });
};

const chooseTemplate = () => {
  DesignChooserActions.openDialog({
    title: "Choose A Blog Template",
    designType: DESIGN_TYPE_WP_PAGE,
  });
};

const BlogScopeCommands = hasBlog
  ? [
      {
        name: "Add a blog template from your library",
        action: chooseTemplate,
        tags: ["add", "create", "wordpress", "wp", "template", "post", "blog"],
        icon: "wordpress",
      },
      {
        name: "Add a blank blog template",
        action: addTemplate,
        tags: ["add", "create", "wordpress", "wp", "template", "new", "post", "blog"],
        icon: "wordpress",
      },
      {
        name: "Add a blog template folder",
        action: addTemplateFolder,
        tags: ["add", "create", "wordpress", "wp", "template", "new", "folder", "blog"],
        icon: "wordpress",
      },
    ]
  : [];

const undo = () => {
  DocumentStore.dispatch({ type: COMMANDS.UNDO });
};

const redo = () => {
  DocumentStore.dispatch({ type: COMMANDS.REDO });
};

const showSiteDesignChooserModal = () => {
  UIActions.showAppView({
    view: MAIN_VIEW_USER_SITE_CHOOSER,
    allowBack: true,
    props: { mode: USER_SITES_VIEW_MODE_MANAGE },
  });
};

const showNewDesignModal = () => {
  UIActions.showAppView({ view: MAIN_VIEW_LIBRARY_SITE_CHOOSER, allowBack: true });
};

const showSharedDesignModal = () => {
  UIActions.modalOpen(MODAL_USER_EXPORTS);
};

const showDesignImportModal = () => {
  UIActions.modalOpen(MODAL_DESIGN_IMPORT);
};

const openSiteSettings = () => {
  SiteSettingsActions.openDialog();
};

const openDesignSettings = () => {
  UIActions.showTheme(THEME_TAB_STYLE);
};

const openFontSettings = () => {
  UIActions.showTheme(THEME_TAB_FONTS);
};

const navigateToHelpDocs = () => {
  window.open("https://learn.showit.com", "_blank");
};

const showIntercom = () => {
  const intercomEnabled = isIntercomEnabled();
  if (intercomEnabled) {
    window.Intercom("show");
  }
};

const showMediaLibrary = () => {
  const mediaLibraryConfig = {
    title: "Add Elements",
    btnLabel: "Add to Page",
  };
  const blockContainer = ActiveBlockContainerStore.getContainer();
  if (blockContainer.get("isBlock")) {
    mediaLibraryConfig.btnLabel = "Add To Site Canvas";
  }
  UIActions.toggleAssetChooser(mediaLibraryConfig);
};

const UICommands = [
  {
    name: "Undo",
    action: undo,
    tags: ["oops"],
    icon: "undo",
  },
  {
    name: "Redo",
    action: redo,
    tags: ["oops"],
    icon: "redo",
  },
  {
    name: "View site designs",
    action: showSiteDesignChooserModal,
    tags: ["library", "change"],
    icon: "mediaLibrary",
  },
  {
    name: "Create new design",
    action: showNewDesignModal,
    tags: ["site", "library", "create"],
    icon: "add",
  },
  {
    name: "View shared designs",
    action: showSharedDesignModal,
    tags: ["exported", "shared", "library"],
    icon: "mediaLibrary",
  },
  {
    name: "Add to library",
    action: showDesignImportModal,
    tags: ["import", "library", "add"],
    icon: "add",
  },
  {
    name: "Open site settings",
    action: openSiteSettings,
    tags: [
      "config",
      "configure",
      "edit",
      "change",
      "update",
      "domain",
      "language",
      "favicon",
      "url",
      "share",
    ],
    icon: "settings",
  },
  {
    name: "Change favicon",
    action: openSiteSettings,
    tags: ["logo", "icon", "branding", "site"],
    icon: "mediaLibrary",
  },
  {
    name: "Manage domain",
    action: openSiteSettings,
    tags: ["url", "domain", "site"],
    icon: "online",
  },
  {
    name: "Change site language",
    action: openSiteSettings,
    tags: ["language", "site"],
    icon: "settings",
  },
  {
    name: "Change site name",
    action: openSiteSettings,
    tags: ["title", "site"],
    icon: "settings",
  },
  {
    name: "Edit social links",
    action: openSiteSettings,
    tags: ["social", "links", "site"],
    icon: "settings",
  },
  {
    name: "Open design settings",
    action: openDesignSettings,
    tags: ["theme", "style", "customize", "edit", "change", "update", "font", "global", "colors"],
    icon: "settings",
  },
  {
    name: "Open media library",
    action: showMediaLibrary,
    tags: ["media", "image", "font", "video", "asset", "upload", "picture", "file", "gallery"],
    icon: "mediaLibrary",
  },
  {
    name: "Manage fonts",
    action: openFontSettings,
    tags: ["theme", "style", "customize", "edit", "change", "update", "font"],
    icon: "titlecase",
  },
  {
    name: "Read help docs",
    action: navigateToHelpDocs,
    tags: ["learn", "help", "docs", "guide", "support", "faq"],
    icon: "learn",
  },
  {
    name: "Message support",
    action: showIntercom,
    tags: ["learn", "help", "docs", "guide", "support", "message", "faq", "intercom"],
    icon: "help",
  },
];

const _toggleOverlay = (overlayType) => {
  UIActions.toggleStageOverlay(overlayType);
};

const zoomInOut = (zoomValue) => {
  const blockContainer = ActiveBlockContainerStore.getContainer();
  const layout = blockContainer.get("layout");
  UIActions.incrementZoom(zoomValue, layout);
};

const zoomTo = (zoomTarget) => {
  const blockContainer = ActiveBlockContainerStore.getContainer();
  const layout = blockContainer.get("layout");
  UIActions.setZoom(zoomTarget, layout);
};

const zoomToWidth = () => {
  const blockContainer = ActiveBlockContainerStore.getContainer();
  const layout = blockContainer.get("layout");
  UIActions.zoomToWidth(layout, blockContainer);
};

const changeStageView = (viewType) => {
  UIActions.changeStageView(viewType);
  if (viewType === STAGE_VIEW_DESKTOP || viewType === STAGE_VIEW_MOBILE) {
    UIActions.selectLayout({ layout: viewType, from: "panel" });
  }
};

const { setUIScale } = UIActions;

const ViewCommands = [
  {
    name: "Show/Hide mobile phone overlay",
    action: () => _toggleOverlay(STAGE_OVERLAY_MOBILE),
    tags: ["ui"],
    icon: "mobileDevice",
  },
  {
    name: "Show/Hide canvas guidelines",
    action: () => _toggleOverlay(STAGE_OVERLAY_GUIDES),
    tags: ["ui", "divider", "guides", "separator"],
    icon: "guides",
  },
  {
    name: "Show/Hide column guidelines",
    action: () => _toggleOverlay(STAGE_OVERLAY_COLUMN),
    tags: ["ui", "divider", "guides", "snapping"],
    icon: "column4x",
  },
  {
    name: "Show/Hide center guidelines",
    action: () => _toggleOverlay(STAGE_OVERLAY_CENTER),
    tags: ["ui", "divider", "guides", "snapping"],
    icon: "add",
  },
  {
    name: "Enable/Disable snapping",
    action: () => _toggleOverlay(STAGE_OVERLAY_SNAP),
    tags: ["ui", "magnet"],
    icon: "alignBottom",
  },
  {
    name: "Zoom in",
    action: () => zoomInOut(ZOOM_INCREMENT),
    tags: ["ui", "big", "magningify", "zooming", "in"],
    icon: "add",
  },
  {
    name: "Zoom out",
    action: () => zoomInOut(-ZOOM_INCREMENT),
    tags: ["ui", "small", "magningify", "zooming", "out"],
    icon: "remove",
  },
  {
    name: "Reset zoom",
    action: () => zoomTo(1),
    tags: ["ui", "default", "magningify", "zooming"],
    icon: "mobileDevice",
  },
  {
    name: "Zoom to width",
    action: () => zoomToWidth(),
    icon: "matchWidth",
  },
  {
    name: "Change to mobile view",
    action: () => changeStageView(STAGE_VIEW_MOBILE),
    tags: ["ui", "phone", "device", "mobile"],
    icon: "mobileDevice",
  },
  {
    name: "Change to desktop view",
    action: () => changeStageView(STAGE_VIEW_DESKTOP),
    tags: ["ui", "computer", "device", "desktop"],
    icon: "desktopDevice",
  },
  {
    name: "Change to split view",
    action: () => changeStageView(STAGE_VIEW_SPLIT),
    tags: ["ui", "split", "device", "desktop"],
    icon: "designSite",
  },
  {
    name: "Set view density to regular",
    action: () => setUIScale(UI_SCALE_REGULAR),
    tags: ["ui", "size", "density", "zoom", "scale", "text", "font", "reset"],
    icon: "matchHW",
  },
  {
    name: "Set view density to large",
    action: () => setUIScale(UI_SCALE_LARGE),
    tags: ["ui", "size", "density", "zoom", "scale", "text", "font", "lg"],
    icon: "matchHW",
  },
  {
    name: "Set view density to small",
    action: () => setUIScale(UI_SCALE_SMALL),
    tags: ["ui", "size", "density", "zoom", "scale", "text", "font", "xl", "xtra"],
    icon: "matchHW",
  },
  {
    name: "Toggle light/dark mode",
    action: () => UIActions.toggleColorMode(),
    tags: ["ui", "light", "dark", "mode", "color"],
    icon: "settings",
  },
];

const SiteAssetCommands = () => {
  const pages = SiteDesignStore.getPageList();
  const siteBlocks = SiteDesignStore.getSiteBlockList();
  const canvasSets = SiteDesignStore.getCanvasSetList();
  return [
    ...pages.map((page) => ({
      name: `Open ${page.get("name")} &nbsp;-&nbsp; pages/${page.get("slug")}`,
      action: () => UIActions.selectPage({ page }),
      tags: ["site", "page", "open", "select", "choose", "view", "show", "edit", "change"],
      icon: "sitePage",
    })),
    ...siteBlocks.map((block) => ({
      name: `Open ${block.get("name")} &nbsp;-&nbsp; site-canvases/${block.get("slug")}`,
      action: () => UIActions.selectSiteBlock({ block }),
      tags: ["site", "canvas", "open", "select", "choose", "view", "show", "edit", "change"],
      icon: "siteCanvas",
      type: "siteCanvas",
    })),
    ...canvasSets.map((canvasSet) => ({
      name: `Open ${canvasSet.get("name")} &nbsp;-&nbsp; canvas-sets/${canvasSet.get("slug")}`,
      action: () => UIActions.selectCanvasSet({ canvasSet }),
      tags: ["site", "canvas-set", "open", "select", "choose", "view", "show", "edit", "change"],
      icon: "canvasSet",
      type: "canvasSet",
    })),
  ];
};

const _selectBlock = (blockContainer, block) => {
  const stageView = DesignViewStore.getStageView();
  if (block.get("selected") == null) {
    const selectedBlock = blockContainer.get("selectedBlock");
    const layout = !selectedBlock.isEmpty()
      ? selectedBlock.getIn(["selected", "layout"])
      : stageView === STAGE_VIEW_SPLIT
      ? STAGE_VIEW_MOBILE
      : stageView;
    UIActions.selectBlock({ id: block.get("id"), layout });
  }
  UIActions.scrollIntoView({ id: block.get("id"), type: "block" });
};

const PageAssetCommands = () => {
  const blockContainer = ActiveBlockContainerStore.getContainer();
  const blocks = blockContainer.get("blocks");

  return blocks
    .filter((block) => !block.get("pBlockId", false))
    .map((block) => ({
      name: `Open ${block.get("name")} &nbsp;-&nbsp; canvases/${block.get("slug")}`,
      action: () => _selectBlock(blockContainer, block),
      tags: ["page", "canvas", "open", "select", "choose", "view", "show", "edit", "change"],
      icon: "pageCanvas",
      type: "pageCanvas",
    }));
};

const UserCommands = [
  {
    name: "Log out",
    action: UserActions.logout,
    tags: ["user", "account", "sign", "out"],
    icon: "accountBox",
  },
];

const CommandPaletteStore = Reflux.createStore({
  storeName: 'CommandPaletteStore',
  listenables: [UIActions, SiteActions],

  init() {
    this.commandPalette = {
      visible: false,
      staticCommands: [
        ...elementCommands,
        ...BlogScopeCommands,
        ...PageScopeCommands,
        ...UICommands,
        ...ViewCommands,
        ...UserCommands,
      ],
      commands: [
        ...elementCommands,
        ...BlogScopeCommands,
        ...PageScopeCommands,
        ...UICommands,
        ...ViewCommands,
        ...UserCommands,
      ],
    };
  },

  onSiteListsUpdated() {
    this.commandPalette = {
      ...this.commandPalette,
      commands: [
        ...this.commandPalette.staticCommands,
        ...PageAssetCommands(),
        ...SiteAssetCommands(),
      ],
    };
    this.trigger(this.commandPalette);
  },

  onPageLoaded() {
    this.commandPalette = {
      ...this.commandPalette,
      commands: [
        ...this.commandPalette.staticCommands,
        ...PageAssetCommands(),
        ...SiteAssetCommands(),
      ],
    };
    this.trigger(this.commandPalette);
  },

  onShowCommandPalette() {
    this.commandPalette = { ...this.commandPalette, visible: !this.commandPalette.visible };
    this.trigger(this.commandPalette);

    UserActions.trackPostHogEvent("command_palette", { action: "open" });
  },

  onHideCommandPalette() {
    this.commandPalette = { ...this.commandPalette, visible: false };
    this.trigger(this.commandPalette);
  },

  getCommandPaletteState() {
    return this.commandPalette;
  },

  getCommands() {
    return this.commands;
  },

  onSelectElement() {
    this.commandPalette = { ...this.commandPalette, visible: false };
    this.trigger(this.commandPalette);
  },

  getInitialState() {
    return this.commandPalette;
  },
});

export default CommandPaletteStore;
